import React, { useContext, useEffect, useReducer, useState } from 'react';
import api from '../api';
import { useLocation, useNavigate } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { Store } from '../Store';
import { getError } from '../utils';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import Card from 'react-bootstrap/Card';
import { Container, Form } from 'react-bootstrap';
import Pagination from '../components/general/Pagination';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' }; // a kept error would outlive the next successful fetch (the render checks it after loading)
    case 'FETCH_SUCCESS':
      return {
        ...state,
        fonts: action.payload.fonts,
        page: action.payload.page,
        pages: action.payload.pages,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    default:
      return state;
  }
};

export default function FontListScreen() {
  const [{ loading, error, fonts, pages }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
  });

  const navigate = useNavigate();
  const { search } = useLocation(); // gets the query parameters of the URL and saves them in the variable called 'search'
  const sp = new URLSearchParams(search); // creates a new instance of the URLSearchParams class, passing in the search string, wich allows to apply several methods to work with the query parameters
  const page = sp.get('page') || 1; // Retrieves the value of the 'page' query parameter (the number on wich page the user is on)
  const searchQuery = sp.get('search') || ''; // Retrieves the 'search' parameter from the URL query string; defaults to an empty string if not present

  const { state } = useContext(Store);
  const { userInfo } = state; // gets the userInfo out of the application state, so that the users token can be used to authorize the fetch request

  const [searchInput, setSearchInput] = useState(searchQuery); // Local state for search input

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        // Include the search parameter in the API request if it exists
        const { data } = await api.get(
          `/api/fonts/admin?page=${page}&search=${encodeURIComponent(
            searchQuery
          )}` // encodeURIComponent: encodes the searchQuery to ensure that special characters are properly formatted in the URL, preventing errors and ensuring accurate query handling
        );
        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err), // as every other list reads a failed request (the screen's copy skipped getError's key extraction, which no message of the font routes carries; chain 10, Oct 2026)
        });
      }
    }; // Fetches fonts based on the current page and search query

    fetchData();
  }, [page, userInfo, searchQuery]);

  const submitHandler = (e) => {
    e.preventDefault();
    const sanitizedInput = searchInput.replace(/\s+/g, ''); // Remove spaces
    navigate(
      sanitizedInput
        ? `/admin/fonts?search=${encodeURIComponent(sanitizedInput)}&page=1`
        : `/admin/fonts?page=1`
    ); // Navigates to the FontListScreen with the search query and resets to page 1 if sanitizedInput exists; otherwise, navigates to page 1 without any search filter
  };

  return (
    <Container>
      <div>
        <title>Fonts</title>

        <h1 className="screen-heading">Fonts</h1>

        {/* Search Form */}
        <Form onSubmit={submitHandler} className="d-flex my-5">
          <Form.Control
            type="text"
            placeholder="Search by Font Family"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search by Font Family"
          />
          <Button type="submit" variant="secondary" className="mx-3">
            Search
          </Button>
        </Form>

        {loading ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <>
            <Row className="d-xl-none">
              {fonts.map((font) => (
                <Col key={font._id} xs={12} sm={12} md={12} lg={6}>
                  <Card className="products-card">
                    <Card.Body className="text-center">
                      <Card.Title className="code mb-4">
                        {font.fontFamily}
                      </Card.Title>
                      <div className="divider"></div>
                      <div className="code-info">
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Additional Styles:
                          </strong>{' '}
                          <span className="no-wrap">
                            {font.hasMoreStyles ? 'Yes' : 'No'}
                          </span>
                        </Card.Text>
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Active:
                          </strong>{' '}
                          <span className="no-wrap">
                            {font.isActive ? 'Yes' : 'No'}
                          </span>
                        </Card.Text>
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Aktionen:
                          </strong>
                          <span className="no-wrap w-100 mt-1">
                            <Button
                              variant="secondary"
                              type="button"
                              onClick={() =>
                                navigate(`/admin/font/${font._id}`)
                              }
                            >
                              Bearbeiten
                            </Button>
                          </span>
                        </Card.Text>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            <div className="d-none d-xl-block">
              <table className="table">
                <thead>
                  <tr>
                    <th>FONT FAMILY</th>
                    <th>ADDITIONAL STYLES</th>
                    <th>ACTIVE</th>
                    <th>AKTIONEN</th>
                  </tr>
                </thead>
                <tbody>
                  {fonts.map((font) => (
                    <tr key={font._id}>
                      <td>{font.fontFamily}</td>
                      <td>{font.hasMoreStyles ? 'Yes' : 'No'}</td>
                      <td>{font.isActive ? 'Yes' : 'No'}</td>
                      <td>
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() => navigate(`/admin/font/${font._id}`)}
                        >
                          Bearbeiten
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              pages={pages}
              page={Number(page)}
              baseUrl="/admin/fonts"
              search={searchQuery}
            />
          </>
        )}
      </div>
    </Container>
  );
}
